/**
 * chess-engine.js —— 学习乐园 · 国际象棋引擎（搜索 + **可解释**的静态评估）
 * ---------------------------------------------------------------------------
 * 为什么自写而不用 Stockfish：产品底线是「对手每走一步，说清它为什么这么走」。
 * 现成强引擎只给一个分数（+0.8），理由是黑箱，写解释只能靠事后编——编出来的理由经常
 * 和真实着法不符。这里的评估项是明写的五项（子力 / 中心 / 发展 / 王的安全 / 威胁），
 * 引擎按这五项的总分选棋，页面按同一份分数的差值给讲解，**理由和着法是同一次计算的两个
 * 输出**，不是两套话术。代价是棋力上限（约业余中级），这是刻意的取舍。
 *
 * 规则层不自写：合法着法、FEN/PGN 由 vendor/chesslib.min.js（chess.js 1.4.0）负责。
 * 本文件只依赖传入的 Chess 实例，不自己 new，故可独立在 node 里测（见 js/__tests__）。
 *
 * 纪律：不发任何请求；不写 DOM；搜索带**时间预算**（迭代加深 + 超时返回已完成的最深一层），
 * 所以低配手机上表现为「算得浅一点」，而不是「卡住几十秒」。
 */
(function (global) {
  'use strict';

  /* ---------------- 常量与查表 ---------------- */

  var VAL = { p: 100, n: 320, b: 335, r: 500, q: 900, k: 0 };
  var CN_NAME = { p: '兵', n: '马', b: '象', r: '车', q: '后', k: '王' };
  var FILES = 'abcdefgh';
  var MATE = 100000;

  var CENTER = { d4: 1, e4: 1, d5: 1, e5: 1 };
  var EXTEND = { c4: 1, f4: 1, c5: 1, f5: 1, d3: 1, e3: 1, d6: 1, e6: 1 };
  // 占中心的价值：中心的兵最值钱（它是结构），轻子次之，重子占中心意义不大
  var CENTER_VAL = { p: 14, n: 8, b: 8, r: 3, q: 2, k: 0 };

  /* 轻子的「自然位置」：初学阶段的教学口径就是「马往中间跳、象瞄准 f7/f2」。
     没有这一项时，引擎会满意地走出 Na3 这类跳边线的棋——它对，因为评估里没说不该跳边；
     但产品是教人的，开局一手上马跳边比棋力不够更致命，所以明写成加分项（同时也是讲解
     「为什么这步好」时能直接指着说的理由）。边线（a/h 列）的马反过来扣一点。 */
  var NATURAL = { n: { c3: 1, f3: 1, c6: 1, f6: 1 }, b: { c4: 1, f4: 1, c5: 1, f5: 1 } };
  var NATURAL_BONUS = 12;
  var RIM_PENALTY = 10;

  var CASTLED_BONUS = 18;   // 已易位（王找到安全位置）的一次性奖励

  /* 三档强度：搜索深度 + 时间预算 + 「放水幅度」（noise，厘兵）。
     入门档的 noise 有意开得大——它会在接近的着法里随机挑，表现出「漏看威胁」，
     这才是刚学会走子的人打得过的对手；不是它算不出更好的棋。 */
  var LEVELS = {
    1: { label: '入门', maxDepth: 2, timeMs: 260, noise: 130, quiesce: false },
    2: { label: '进阶', maxDepth: 3, timeMs: 800, noise: 0, quiesce: true },
    3: { label: '挑战', maxDepth: 5, timeMs: 1800, noise: 0, quiesce: true }
  };

  /* 超时哨兵：搜索用异常展开回溯，避免「一半结果」被当成完整结果返回 */
  var TIMEOUT = { timeout: true };

  function sqName(r, c) { return FILES.charAt(c) + String(8 - r); }

  /* ---------------- 静态评估（五项，白方视角，单位厘兵） ----------------
     board() 返回 board[0] = 第 8 横排，故 r 方向即「白方向前」。 */

  function at(board, r, c) {
    if (r < 0 || r > 7 || c < 0 || c > 7) { return null; }
    return board[r][c];
  }

  /** (r,c) 是否被 byColor 的子攻击到：兵 / 马 / 王邻格 + 车象后射线 */
  function attacked(board, r, c, byColor) {
    var pc, dr, dc, i, rr, cc;
    // 兵：白兵从下方斜吃（r+1），黑兵从上方斜吃（r-1）
    var pawnRank = byColor === 'w' ? 1 : -1;
    for (i = -1; i <= 1; i += 2) {
      pc = at(board, r + pawnRank, c + i);
      if (pc && pc.color === byColor && pc.type === 'p') { return true; }
    }
    var KN = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]];
    for (i = 0; i < 8; i++) {
      pc = at(board, r + KN[i][0], c + KN[i][1]);
      if (pc && pc.color === byColor && pc.type === 'n') { return true; }
    }
    for (dr = -1; dr <= 1; dr++) {
      for (dc = -1; dc <= 1; dc++) {
        if (!dr && !dc) { continue; }
        pc = at(board, r + dr, c + dc);
        if (pc && pc.color === byColor && pc.type === 'k') { return true; }
      }
    }
    var ROOK_D = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    var BISH_D = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
    for (i = 0; i < 4; i++) {
      rr = r + ROOK_D[i][0]; cc = c + ROOK_D[i][1];
      while (rr >= 0 && rr <= 7 && cc >= 0 && cc <= 7) {
        pc = board[rr][cc];
        if (pc) {
          if (pc.color === byColor && (pc.type === 'r' || pc.type === 'q')) { return true; }
          break;
        }
        rr += ROOK_D[i][0]; cc += ROOK_D[i][1];
      }
      rr = r + BISH_D[i][0]; cc = c + BISH_D[i][1];
      while (rr >= 0 && rr <= 7 && cc >= 0 && cc <= 7) {
        pc = board[rr][cc];
        if (pc) {
          if (pc.color === byColor && (pc.type === 'b' || pc.type === 'q')) { return true; }
          break;
        }
        rr += BISH_D[i][0]; cc += BISH_D[i][1];
      }
    }
    return false;
  }

  /** 是否有 byColor 的子保护 (r,c)。与 attacked 同一个判定——「某个 byColor 的子能吃进来」
      就是「这个格子被 byColor 守着」；(r,c) 上的子本身不会被扫到，所以不必额外排除自身。 */
  function defended(board, r, c, byColor) {
    return attacked(board, r, c, byColor);
  }

  /** 王前兵盾 + 暴露开放列的扣分（只算王所在的三列，成本可控） */
  function kingShield(board, kr, kc, color) {
    var score = 0, forward = color === 'w' ? -1 : 1, r, c, pc;
    for (c = kc - 1; c <= kc + 1; c++) {
      r = kr + forward;
      pc = at(board, r, c);
      if (pc && pc.color === color && pc.type === 'p') { score += 12; }
    }
    // 王前一格被清空是危险信号（实战里被攻王多半从这里开始）
    pc = at(board, kr + forward, kc);
    if (!pc) { score -= 10; }
    return score;
  }

  /** 静态评估：返回五项分解 + 总分（白方视角） */
  function evaluate(game) {
    var board = game.board();
    var material = 0, center = 0, development = 0, kingSafety = 0, threats = 0;
    var kings = { w: null, b: null };
    var r, c, i, pc, sign, sq, hangW = 0, hangB = 0;

    for (r = 0; r < 8; r++) {
      for (c = 0; c < 8; c++) {
        pc = board[r][c];
        if (!pc) { continue; }
        sign = pc.color === 'w' ? 1 : -1;
        sq = sqName(r, c);
        material += sign * VAL[pc.type];

        if (CENTER[sq]) { center += sign * CENTER_VAL[pc.type]; }
        else if (EXTEND[sq]) { center += sign * Math.round(CENTER_VAL[pc.type] / 2); }

        // 发展：轻子还压在底排 = 没出动；后过早跑到中路不算发展（这里只惩罚未出动）
        if (pc.type === 'n' || pc.type === 'b') {
          if (pc.color === 'w' && r === 7) { development -= 12; }
          if (pc.color === 'b' && r === 0) { development += 12; }
          if (NATURAL[pc.type][sq]) { development += sign * NATURAL_BONUS; }
          // 边线马：跳边的那一步几乎不出力（开局 Na3 的账就记在这里）
          if (pc.type === 'n' && (c === 0 || c === 7)) { development -= sign * RIM_PENALTY; }
        }

        if (pc.type === 'k') {
          kings[pc.color] = { r: r, c: c };
          // 王已离开原位（多数是易位，少数是往外逃）= 找到了安全位置，给一次性奖励；
          // 还停在原位不给分——「没易位」不是扣分项，扣的是后面 kingShield 里的薄盾
          var home = pc.color === 'w' ? (r === 7 && c === 4) : (r === 0 && c === 4);
          if (!home) { kingSafety += sign * CASTLED_BONUS; }
        }
      }
    }

    for (i = 0; i < 2; i++) {
      var color = i === 0 ? 'w' : 'b';
      var k = kings[color];
      if (!k) { continue; }
      var s = kingShield(board, k.r, k.c, color);
      kingSafety += color === 'w' ? s : -s;
    }

    /* 威胁：被对方攻击且己方无人保护的子（俗称「悬子」）。
       这是「这一步是不是真的盯着什么东西」的可计算版本——它比「机动性」更好解释，
       也更贴近初学者能听懂的话：「白后没人保护，被黑马盯着」。 */
    for (r = 0; r < 8; r++) {
      for (c = 0; c < 8; c++) {
        pc = board[r][c];
        if (!pc || pc.type === 'k') { continue; }
        var enemy = pc.color === 'w' ? 'b' : 'w';
        if (attacked(board, r, c, enemy) && !defended(board, r, c, pc.color)) {
          if (pc.color === 'w') { hangW += VAL[pc.type]; } else { hangB += VAL[pc.type]; }
        }
      }
    }
    // 悬子按 0.6 折：它不是已经丢的兵，只是「有丢的风险」
    threats = Math.round((hangB - hangW) * 0.6);

    var total = material + center + development + kingSafety + threats;
    return {
      total: total,
      material: material,
      center: center,
      development: development,
      kingSafety: kingSafety,
      threats: threats
    };
  }

  /* ---------------- 搜索（negamax + alpha-beta + 静态化延伸） ----------------
     顺序刻意是「先判和 → 取着法 → 无着法即杀/逼和」：isGameOver() 内部自己生成着法，
     每个节点都调它等于把着法生成做两遍，深度 3 就慢得没法用了。 */

  function orderMoves(moves) {
    for (var i = 0; i < moves.length; i++) { moves[i].ord = scoreMove(moves[i]); }
    moves.sort(function (a, b) { return b.ord - a.ord; });
    return moves;
  }
  function scoreMove(m) {
    var s = 0;
    if (m.captured) { s += 1000 + VAL[m.captured] * 10 - VAL[m.piece]; }
    if (m.promotion) { s += 900 + VAL[m.promotion]; }
    if (m.san && m.san.indexOf('+') > -1) { s += 30; }
    return s;
  }

  function moveInput(m) {
    return m.promotion ? { from: m.from, to: m.to, promotion: m.promotion }
      : { from: m.from, to: m.to };
  }

  var nodes = 0;
  var deadline = 0;

  /* 搜索期「已落子 / 未撤销」计数。超时是从递归中间抛出的（tick 抛 TIMEOUT），异常会跳过
     沿途的 game.undo()，调用方那块棋盘就留在搜索中途——对手那一步随后就成了非法着法，
     讲解也会对着一个页面从未出现过的局面说话。所以每次落子都计数，退出前一律补撤到干净。 */
  var made = 0;

  function play(game, mv) { game.move(mv); made++; }
  function unplay(game) { game.undo(); made--; }
  function unwind(game) { while (made > 0) { game.undo(); made--; } }

  function tick() {
    if ((++nodes & 511) === 0 && Date.now() > deadline) { throw TIMEOUT; }
  }

  /** 静态化延伸：只继续算吃子，把「一下吃子就崩」的噪声压掉（level 2/3 才开） */
  function quiesce(game, alpha, beta) {
    tick();
    var standPat = evaluate(game).total * (game.turn() === 'w' ? 1 : -1);
    if (standPat >= beta) { return beta; }
    if (standPat > alpha) { alpha = standPat; }
    var moves = orderMoves(game.moves({ verbose: true }).filter(function (m) { return m.captured; }));
    for (var i = 0; i < moves.length; i++) {
      play(game, moveInput(moves[i]));
      var s = -quiesce(game, -beta, -alpha);
      unplay(game);
      if (s >= beta) { return beta; }
      if (s > alpha) { alpha = s; }
    }
    return alpha;
  }

  /** negamax：返回值一律是「轮走方视角」的分 */
  function negamax(game, depth, alpha, beta, ply, useQ) {
    tick();
    if (depth <= 0) {
      if (game.isDraw()) { return 0; }
      return useQ ? quiesce(game, alpha, beta)
        : evaluate(game).total * (game.turn() === 'w' ? 1 : -1);
    }
    var moves = orderMoves(game.moves({ verbose: true }));
    if (!moves.length) {
      // 没子可走：被将军就是将杀，没被将军就是逼和（和棋，0 分）
      return game.inCheck() ? -MATE + ply : 0;
    }
    var best = -Infinity;
    for (var i = 0; i < moves.length; i++) {
      play(game, moveInput(moves[i]));
      var s = -negamax(game, depth - 1, -beta, -alpha, ply + 1, useQ);
      unplay(game);
      if (s > best) { best = s; }
      if (s > alpha) { alpha = s; }
      if (alpha >= beta) { break; }   // 剪枝：这条路对对手更好，他不会走进来
    }
    return best;
  }

  /* ---------------- 讲解文本（五项差值 → 大白话） ---------------- */

  var TERM_CN = {
    material: '子力', center: '中心', development: '发展',
    kingSafety: '王的安全', threats: '威胁'
  };

  function signedText(v) {
    var cp = Math.abs(v);
    if (cp >= 100) { return (cp / 100).toFixed(1).replace(/\.0$/, '') + ' 个兵'; }
    return cp + ' 厘兵';
  }

  /**
   * 由「五项差值」组织一句理由。dir 为行棋方视角（+1 白 / -1 黑），
   * 只讲绝对值最大的一到两项——初学者的注意力装不下三条以上理由。
   */
  function phrasesFor(deltas, extra) {
    var out = [];
    if (extra && extra.length) { out = out.concat(extra); }
    var keys = ['material', 'center', 'development', 'kingSafety', 'threats'];
    var ranked = keys.map(function (k) { return { k: k, v: deltas[k] }; })
      .filter(function (x) { return Math.abs(x.v) >= 12; })
      .sort(function (a, b) { return Math.abs(b.v) - Math.abs(a.v); });
    for (var i = 0; i < ranked.length && out.length < 3; i++) {
      var x = ranked[i];
      var good = x.v > 0;
      if (x.k === 'material') {
        out.push((good ? '多吃 ' : '少 ') + signedText(x.v));
      } else if (x.k === 'center') {
        out.push(good ? '把中心格占得更牢' : '中心让出去了');
      } else if (x.k === 'development') {
        out.push(good ? '子力出动、后面更有劲' : '还有子压在底排没出动');
      } else if (x.k === 'kingSafety') {
        out.push(good ? '王的位置更安全' : '王前面的掩护变薄了');
      } else if (x.k === 'threats') {
        out.push(good ? '盯住了对方没人保护的子' : '自己的子有被吃的风险');
      }
    }
    return out;
  }

  /**
   * 讲解一步棋：传入「走之前」的 game 与着法对象（verbose move），内部 make/undo，
   * 返回五项前后差与理由。游戏 js 用它给行子里每一步都标注「好在哪、亏在哪」。
   */
  function annotate(game, mv) {
    var mover = game.turn();
    var before = evaluate(game);
    game.move(moveInput(mv));
    var after = evaluate(game);
    var inCheck = game.inCheck && game.inCheck();
    var mate = game.isCheckmate && game.isCheckmate();
    game.undo();

    var dir = mover === 'w' ? 1 : -1;
    var deltas = {};
    Object.keys(before).forEach(function (k) {
      deltas[k] = Math.round((after[k] - before[k]) * dir);
    });
    var extra = [];
    if (mate) { extra.push('这步直接把死'); }
    else if (inCheck) { extra.push('把对方将军了'); }
    if (mv.captured) { extra.push('吃掉对方的' + CN_NAME[mv.captured]); }
    if (mv.promotion) { extra.push('兵升变成' + CN_NAME[mv.promotion]); }
    if (mv.san && (mv.san.indexOf('O-O') === 0 || mv.san.indexOf('0-0') === 0)) {
      extra.push('王车易位，王收到安全处');
    }
    var score = Math.round(after.total * dir) - Math.round(before.total * dir);
    return {
      san: mv.san, mover: mover, deltas: deltas, score: score,
      // check：走完这步之后，接棋的一方是否被将军（界面据此高亮王，不必自己再判一遍）
      check: !!inCheck, mate: !!mate,
      phrases: phrasesFor(deltas, extra),
      before: before, after: after
    };
  }

  /* ---------------- 选棋 ---------------- */

  /**
   * bestMove：迭代加深 + 时间预算。超时不报错，返回**已完成的最深一层**的结果——
   * 低配手机的表现为「算得浅一点」，而不是页面卡死。
   * timeMs 可选：复盘页把单局面预算调小（整局 N+1 次搜索，省下来的时间是分钟级），
   * 对战页不传，行为与从前完全一致。
   * 返回 { move, score（白方视角厘兵）, depth（实际算到的层数）, level,
   *        top（≤3 条候选：{san, loss}，loss = 与最佳着法的分差，轮走方视角厘——
   *        复盘页「引擎建议 / 备选」直接用，对战页零感知） }
   */
  function bestMove(game, level, timeMs) {
    var cfg = LEVELS[level] || LEVELS[2];
    var rootMoves = orderMoves(game.moves({ verbose: true }));
    if (!rootMoves.length) { return null; }
    var mover = game.turn();
    var dir = mover === 'w' ? 1 : -1;

    // 只有一步可走时不必搜索（也将杀题、逼和这类唯一应着的表现变得稳定）
    if (rootMoves.length === 1) {
      return { move: rootMoves[0], score: 0, depth: 1, level: level,
        top: [{ san: rootMoves[0].san, loss: 0 }] };
    }

    deadline = Date.now() + (typeof timeMs === 'number' && timeMs > 0 ? timeMs : cfg.timeMs);
    nodes = 0;
    var best = rootMoves[0], bestScore = -Infinity, done = 0, round = null;

    /* 迭代加深整体套 finally：超时（TIMEOUT）是从递归中途抛上来的，异常路径上的落子
       不会被沿途的 unplay 撤掉，必须在这里统一补撤，否则调用方的棋盘留在搜索中途。 */
    try {
      for (var depth = 1; depth <= cfg.maxDepth; depth++) {
        var scored = [];
        var alpha = -Infinity, roundBest = null;
        try {
          for (var i = 0; i < rootMoves.length; i++) {
            var m = rootMoves[i];
            play(game, moveInput(m));
            var s = -negamax(game, depth - 1, -Infinity, -alpha, 1, cfg.quiesce);
            unplay(game);
            scored.push({ move: m, score: s });
            if (s > alpha) { alpha = s; roundBest = m; }
          }
        } catch (e) {
          if (e !== TIMEOUT) { throw e; }
          break;   // 本层没算完 → 结果作废，保留上一层（done / bestScore / round）的结论
        }
        // 本层算完才提交：bestScore 与 round 里的 score 必须同一口径，混层比较会挑出错着
        done = depth;
        round = scored;
        bestScore = alpha;
        if (roundBest) { best = roundBest; }
        if (bestScore > MATE - 64) { break; }   // 已找到杀，不必再深挖
        scored.sort(function (a, b) { return b.score - a.score; });
        rootMoves = scored.map(function (x) { return x.move; });  // 下一层最好的一条先算
      }
    } finally {
      unwind(game);
    }

    /* 放水：在「与最佳着法相差不超过 noise」的着法里随机挑一条。
       入门档靠它打出「会漏看威胁」的手感——它不是算不出来，是算到了也不选。
       比较用的是最后一层完整结果，所以分差口径一致。 */
    if (cfg.noise > 0 && round) {
      var pool = round.filter(function (x) { return bestScore - x.score <= cfg.noise; });
      if (pool.length > 1) {
        best = pool[Math.floor(Math.random() * pool.length)].move;
      }
    }

    /* 候选榜：取最后一层完整结果的前 3 名（同一口径，放水前的真实排序）。
       复盘页用它给「引擎建议 + 备选 + 分差」——这正是付费复盘「选点太少」的对立面。 */
    var top = null;
    if (round) {
      var ranked = round.slice().sort(function (a, b) { return b.score - a.score; });
      top = [];
      for (var t = 0; t < ranked.length && t < 3; t++) {
        top.push({ san: ranked[t].move.san, loss: Math.max(0, Math.round(bestScore - ranked[t].score)) });
      }
    }

    /* 极端兜底：连第 1 层都没算完（预算给得太小/设备太慢）时 bestScore 还是
       -Infinity——不拦住会顺着 score 流进复盘页，亏分变 Infinity、进存档再
       JSON 序列化成 null。回退 0（近似均衡），调用方拿到的永远是有限数。 */
    var outScore = isFinite(bestScore) ? Math.round(bestScore * dir) : 0;
    return { move: best, score: outScore, depth: done || 1, level: level, top: top };
  }

  /* ---------------- PV 线（复盘页按需调用） ---------------- */

  /**
   * pvNegamax：带 PV 收集的 negamax。每层把自己的最佳着法接在子线前面，
   * 根节点收到的就是完整主变。剪枝后的线不完整——展示用途可接受，如实不冒充。
   */
  function pvNegamax(game, depth, alpha, beta, ply, line, useQ) {
    tick();
    if (depth <= 0) {
      if (game.isDraw()) { return 0; }
      return useQ ? quiesce(game, alpha, beta)
        : evaluate(game).total * (game.turn() === 'w' ? 1 : -1);
    }
    var moves = orderMoves(game.moves({ verbose: true }));
    if (!moves.length) { return game.inCheck() ? -MATE + ply : 0; }
    var best = -Infinity, bestLine = null;
    for (var i = 0; i < moves.length; i++) {
      play(game, moveInput(moves[i]));
      var child = [];
      var s = -pvNegamax(game, depth - 1, -beta, -alpha, ply + 1, child, useQ);
      unplay(game);
      if (s > best) { best = s; bestLine = [moves[i].san].concat(child); }
      if (s > alpha) { alpha = s; }
      if (alpha >= beta) { break; }
    }
    if (bestLine) { for (var j = 0; j < bestLine.length; j++) { line.push(bestLine[j]); } }
    return best;
  }

  /**
   * pvLine：给定局面，算一条「双方都走最强」的延伸（SAN 数组，约 2~3 步）。
   * 复盘页点开某条结论时才算（一次一个局面，几百毫秒），不进 analyze 主循环。
   * 超时返回已收集的部分（可能为空），棋盘保证复原。
   */
  function pvLine(game, level) {
    var cfg = LEVELS[level] || LEVELS[2];
    if (!game.moves().length) { return []; }
    deadline = Date.now() + Math.max(300, Math.min(cfg.timeMs, 600));
    nodes = 0;
    var line = [];
    try {
      pvNegamax(game, Math.min(3, cfg.maxDepth), -Infinity, Infinity, 0, line, !!cfg.quiesce);
    } catch (e) {
      if (e !== TIMEOUT) { throw e; }
      // 超时：line 保留已有前缀（可能为空），调用方按「没有变化线」处理
    } finally {
      unwind(game);
    }
    return line;
  }

  /* ---------------- 出口 ---------------- */

  global.STChessEngine = {
    LEVELS: LEVELS,
    VALUES: VAL,
    TERMS: TERM_CN,
    evaluate: evaluate,
    bestMove: bestMove,
    pvLine: pvLine,
    annotate: annotate,
    // 供冒烟与未来 M2 复盘复用：把「分差」翻成 失误 / 错着 / 漏着 三档的口径
    classify: function (loss) {
      if (loss >= 180) { return 'blunder'; }
      if (loss >= 80) { return 'mistake'; }
      if (loss >= 35) { return 'inaccuracy'; }
      return 'ok';
    }
  };
})(typeof window !== 'undefined' ? window : globalThis);
